"use client";

import { useSyncExternalStore } from "react";

const HEALTH_POLL_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Module-level singleton state
// ---------------------------------------------------------------------------
//
// Why a singleton: ``useThatradiothingOnline`` is consumed in more than one
// place (the home page's inline radio card and the floating dock that lives
// in ``SiteShell``). On the home page both consumers mount at once, so a
// naive "one interval per hook call" implementation fires two
// ``/api/radio/health`` requests every 30 s. The Next route in-process
// caches the upstream probe, so the duplicate cost is small server-side,
// but it still doubles the browser-to-Next round-trips for no reason.
//
// We replace that with a single shared polling loop:
//
//   • ``subscribers``     — every active hook adds its setter here so
//                            updates fan out to all consumers from one
//                            poll.
//   • ``pollTimerId``     — the lone ``setInterval`` token. Lives only
//                            while at least one subscriber is mounted.
//   • ``inflight``        — guards against overlapping fetches (e.g. when a
//                            new subscriber mounts while a poll is mid-air).
//   • ``latest``          — last known result; new subscribers seed from it
//                            so the second mount doesn't trigger a fresh
//                            request just to learn what we already know.
//
// Pure module state is fine here: this file is ``"use client"``, so the
// singleton is per-tab. Nothing else touches it.
// ---------------------------------------------------------------------------

type Subscriber = () => void;

const subscribers = new Set<Subscriber>();
let pollTimerId: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<void> | null = null;
let latest: boolean | null = null;

async function probe(): Promise<void> {
  // Coalesce concurrent probes: if a fetch is already in flight, every
  // caller awaits the same promise instead of starting a new request.
  if (inflight) {
    return inflight;
  }
  inflight = (async () => {
    try {
      const response = await fetch("/api/radio/health", { cache: "no-store" });
      if (!response.ok) {
        latest = false;
      } else {
        const data = (await response.json()) as { online?: boolean };
        latest = Boolean(data?.online);
      }
    } catch {
      latest = false;
    } finally {
      inflight = null;
    }
    // Fan out to everyone who is currently mounted. We snapshot the set
    // before iterating because a subscriber's setState could synchronously
    // unmount and remove itself, which would otherwise mutate the live set
    // mid-iteration.
    for (const subscriber of [...subscribers]) {
      subscriber();
    }
  })();
  return inflight;
}

function ensurePollingStarted() {
  if (pollTimerId !== null) {
    return;
  }
  // Kick off an immediate probe for the first subscriber so they don't sit
  // on ``null`` for a full 30 s — but skip it if a result already exists
  // (a previous subscriber primed the cache and we just haven't expired
  // yet).
  if (latest === null) {
    void probe();
  }
  pollTimerId = setInterval(() => {
    void probe();
  }, HEALTH_POLL_INTERVAL_MS);
}

function ensurePollingStopped() {
  if (subscribers.size > 0) {
    return;
  }
  if (pollTimerId !== null) {
    clearInterval(pollTimerId);
    pollTimerId = null;
  }
  // Intentionally keep ``latest`` so a remount within the same session
  // (e.g. closing and reopening the floating dock) doesn't flicker through
  // ``null`` again.
}

// Client hook that exposes a shared liveness signal for thatradiothing.
//
// Returns:
//   null  — first probe in flight; render optimistically so online users
//           never see a flash of "offline" UI.
//   true  — service reachable.
//   false — service confirmed unreachable; callers should hide the radio
//           module and reclaim its layout slot.
export function useThatradiothingOnline(): boolean | null {
  return useSyncExternalStore(
    (notify) => {
      subscribers.add(notify);
      ensurePollingStarted();
      // Prime late subscribers if a prior probe already has a cached
      // answer. Queueing keeps the notification outside the subscribe
      // call itself, which matches the `useSyncExternalStore`
      // expectation that subscriptions are passive wiring.
      if (latest !== null) {
        queueMicrotask(notify);
      }
      return () => {
        subscribers.delete(notify);
        ensurePollingStopped();
      };
    },
    () => latest,
    () => null,
  );
}
